/**
 * Links in the M7 rail: finding them in what you typed or what the agent wrote,
 * and naming what they point at from the address alone.
 *
 * There is no preview service yet (creative-os-backend#99), so everything here
 * is read off the URL: the platform, the kind of post, and YouTube's public
 * poster. A title, a summary or a page image arrive with that service.
 */

export type RailLink = {
  url: string;
  /** "nourapantry.com", without www. */
  host: string;
  /** The path as it reads, trimmed: "/p/C8xR2kLpQma". Empty for a site's root. */
  path: string;
  /** A PlatformGlyph key ("YOUTUBE", "TIKTOK"...), or null for any other site. */
  platform: string | null;
  /** What it is: "YouTube video", "Instagram reel", or the host for a page. */
  kind: string;
  /** The words the agent linked, when it wrote [words](address). */
  title?: string;
  /** A video platform's link, shown as media rather than a card. */
  video: boolean;
  /** YouTube's public poster, the one preview the browser can load by itself. */
  poster: string | null;
};

/* http(s) addresses only. The closing class drops the punctuation a sentence
   puts after a link ("see https://x.com/a." ends at /a). */
const URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+[^\s<>"'`.,;:!?)\]]/gi;

function youtubeId(url: URL): string | null {
  const host = url.hostname.replace(/^www\.|^m\./, "");
  if (host === "youtu.be") return url.pathname.slice(1).split("/")[0] || null;
  if (host !== "youtube.com") return null;
  if (url.pathname === "/watch") return url.searchParams.get("v");
  const match = url.pathname.match(/^\/(?:shorts|embed|live)\/([^/?#]+)/);
  return match?.[1] ?? null;
}

function describe(raw: string): RailLink | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.replace(/^www\./, "");
  const path = url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "");
  const base = { url: url.href, host, path, poster: null as string | null };

  const yt = youtubeId(url);
  if (yt) {
    return {
      ...base,
      platform: "YOUTUBE",
      kind: url.pathname.startsWith("/shorts/") ? "YouTube Short" : "YouTube video",
      video: true,
      poster: `https://i.ytimg.com/vi/${encodeURIComponent(yt)}/mqdefault.jpg`,
    };
  }
  if (host.endsWith("tiktok.com")) {
    const post = /\/(?:video|photo)\/\d+/.test(url.pathname) || /^(?:vm|vt)\./.test(host) || url.pathname.startsWith("/t/");
    return { ...base, platform: "TIKTOK", kind: post ? "TikTok video" : "TikTok", video: post };
  }
  if (host === "instagram.com") {
    const reel = /^\/(?:[^/]+\/)?reels?\//.test(url.pathname);
    const post = /^\/(?:[^/]+\/)?(?:p|tv)\//.test(url.pathname);
    return {
      ...base,
      platform: "INSTAGRAM",
      kind: reel ? "Instagram reel" : post ? "Instagram post" : "Instagram",
      video: reel,
    };
  }
  const named: Array<[RegExp, string, string]> = [
    [/(^|\.)facebook\.com$|^fb\.watch$/, "FACEBOOK", "Facebook"],
    [/(^|\.)linkedin\.com$/, "LINKEDIN", "LinkedIn"],
    [/(^|\.)pinterest\.[a-z.]+$|^pin\.it$/, "PINTEREST", "Pinterest"],
    [/(^|\.)snapchat\.com$/, "SNAPCHAT", "Snapchat"],
    [/(^|\.)reddit\.com$/, "REDDIT", "Reddit"],
    [/(^|\.)substack\.com$/, "SUBSTACK", "Substack"],
  ];
  for (const [pattern, platform, kind] of named) {
    if (pattern.test(host)) return { ...base, platform, kind, video: false };
  }
  return { ...base, platform: null, kind: host, video: false };
}

/** Every link in a piece of text, first appearance only, at most `limit`. */
export function linksIn(text: string, limit = 3): RailLink[] {
  const seen = new Set<string>();
  const links: RailLink[] = [];
  for (const match of text.matchAll(URL_PATTERN)) {
    const link = describe(match[0]);
    if (!link || seen.has(link.url)) continue;
    seen.add(link.url);
    links.push(link);
    if (links.length >= limit) break;
  }
  return links;
}

/**
 * The links at the very end of a message, split from the words before them. A
 * link you end on becomes its embed, so the bubble drops the address; a link in
 * the middle of a sentence stays where you put it.
 */
export function splitTrailingLinks(text: string): { body: string; links: RailLink[] } {
  const words = text.trimEnd().split(/(\s+)/);
  const tail: string[] = [];
  const whole = new RegExp(`^${URL_PATTERN.source}$`, "i");
  while (words.length > 0) {
    const last = words[words.length - 1]!;
    if (!last.trim()) {
      words.pop();
      continue;
    }
    // "…/ramadan." ends a sentence on a link: the full stop is not the link's.
    const bare = last.replace(/[.,;:!?)\]]+$/, "");
    if (!whole.test(bare)) break;
    tail.unshift(bare);
    words.pop();
  }
  if (tail.length === 0) return { body: text, links: [] };
  const links = tail.map(describe).filter((link): link is RailLink => link !== null);
  return { body: words.join("").trimEnd(), links };
}

/* A line that is only a link: a bare address, <address>, or [label](address). */
const LINK_LINE = /^\s*(?:<(https?:\/\/[^\s>]+)>|\[([^\]\n]*)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/\S+))\s*$/i;

/**
 * The links the agent put on their own lines, lifted out of its reply so they
 * show as embeds. Links inside a sentence stay in the text as links.
 */
export function standaloneLinks(markdown: string): { text: string; links: RailLink[] } {
  const links: RailLink[] = [];
  const kept: string[] = [];
  let fenced = false;
  for (const line of markdown.split("\n")) {
    // Code is left exactly as written.
    if (/^\s*(```|~~~)/.test(line)) fenced = !fenced;
    const match = fenced ? null : line.match(LINK_LINE);
    const described = match ? describe(match[1] ?? match[3] ?? match[4] ?? "") : null;
    const label = match?.[2]?.trim();
    const link = described && label ? { ...described, title: label } : described;
    if (link && !links.some((l) => l.url === link.url)) {
      links.push(link);
      continue;
    }
    kept.push(line);
  }
  if (links.length === 0) return { text: markdown, links };
  return { text: kept.join("\n").replace(/\n{3,}/g, "\n\n").trim(), links };
}

/** A site's icon, from the same service the Signals thumbnails use. */
export function faviconUrl(host: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
}

/** "nourapantry.com/ramadan", shortened for one line. */
export function shortAddress(link: RailLink, max = 42): string {
  const full = `${link.host}${link.path}`;
  return full.length > max ? `${full.slice(0, max - 1)}…` : full;
}
